/** Hilfsfunktionen der Zeiterfassung (Formatierung, Ortszeit Europe/Berlin, Monate). */
import { useEffect, useState } from 'react';
import type { AbsenceKind, Employment } from './types';

export const TZ = 'Europe/Berlin';

export const ABSENCE_LABELS: Record<AbsenceKind, string> = {
  urlaub: 'Urlaub',
  krank: 'Krank',
  frei: 'Frei',
  feiertag_bezahlt: 'Feiertag (bezahlt)',
  sonstiges: 'Sonstiges',
};
export const ABSENCE_HINTS: Record<AbsenceKind, string> = {
  urlaub: 'Bezahlter Urlaub – zählt mit den Stunden auf das Soll.',
  krank: 'Arbeitsunfähig (Lohnfortzahlung) – zählt mit den Stunden auf das Soll.',
  frei: 'Freier Tag ohne Gutschrift, z. B. Überstundenabbau oder unbezahlt.',
  feiertag_bezahlt: 'Für Aushilfen/Minijobber, die am Feiertag sonst gearbeitet hätten (Lohnfortzahlung).',
  sonstiges: 'z. B. Schulung, Behördengang – mit frei wählbaren Stunden.',
};
export const ABSENCE_KINDS = Object.keys(ABSENCE_LABELS) as AbsenceKind[];

export const EMPLOYMENT_LABELS: Record<Employment, string> = {
  vollzeit: 'Vollzeit',
  teilzeit: 'Teilzeit',
  minijob: 'Minijob',
  aushilfe: 'Aushilfe',
};

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

/** Minuten → "7:45" (negativ mit Minus). */
export function hm(minutes: number | null | undefined): string {
  if (minutes == null) return '–';
  const sign = minutes < 0 ? '−' : '';
  const m = Math.abs(Math.round(minutes));
  return `${sign}${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}
/** Minuten → "7:45 h" */
export const hmh = (minutes: number | null | undefined) => (minutes == null ? '–' : `${hm(minutes)} h`);
/** Minuten mit Vorzeichen → "+1:30 h" */
export const hmSigned = (minutes: number | null | undefined) => (minutes == null ? '–' : `${minutes > 0 ? '+' : ''}${hm(minutes)} h`);
/** Stunden (Dezimal) → "7,5" */
export const decHours = (h: number) => h.toLocaleString('de-DE', { maximumFractionDigits: 2 });

const partsFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
function parts(ms: number) {
  const p = Object.fromEntries(partsFmt.formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  return { y: +p.year, mo: +p.month, d: +p.day, h: +p.hour, mi: +p.minute };
}
const pad = (n: number) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' in Ortszeit */
export function localDate(ms: number): string {
  const p = parts(ms);
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}`;
}
/** 'HH:MM' in Ortszeit */
export function localTime(ms: number): string {
  const p = parts(ms);
  return `${pad(p.h)}:${pad(p.mi)}`;
}
export const todayLocal = () => localDate(Date.now());
export const currentMonth = () => todayLocal().slice(0, 7);

/** Ortszeit (Datum + 'HH:MM') → Unix-ms (berücksichtigt Sommer-/Winterzeit). */
export function zonedToMs(date: string, time: string): number {
  const [y, m, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  let guess = Date.UTC(y, m - 1, d, h, mi);
  for (let i = 0; i < 2; i++) {
    const p = parts(guess);
    guess += Date.UTC(y, m - 1, d, h, mi) - Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi);
  }
  return guess;
}

export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}
export function weekStart(date: string): string {
  const wd = new Date(date + 'T12:00:00Z').getUTCDay() || 7;
  return addDays(date, 1 - wd);
}
export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}`;
}
/** '2026-10' → "Oktober 2026" */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}
/** 'YYYY-MM-DD' → "05.10." */
export const dShort = (date: string) => `${date.slice(8, 10)}.${date.slice(5, 7)}.`;
/** 'YYYY-MM-DD' → "05.10.2026" */
export const dLong = (date: string | null | undefined) => (date ? `${date.slice(8, 10)}.${date.slice(5, 7)}.${date.slice(0, 4)}` : '–');

/** "08:02" (heute), "gestern 22:10" oder "05.10. 08:02" */
export function sinceLabel(ms: number, now = Date.now()): string {
  const d = localDate(ms);
  const today = localDate(now);
  const t = localTime(ms);
  if (d === today) return t;
  if (addDays(d, 1) === today) return `gestern ${t}`;
  return `${dShort(d)} ${t}`;
}

/** Erneuert die Anzeige regelmäßig (laufende Zeiten). */
export function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** "Max Mustermann" → "MM" */
export function initials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? p[p.length - 1][0] : p[0]?.[1] ?? '')).toUpperCase();
}

/** Gesetzlicher Mindestlohn je Stunde in Cent (Stand: Beschluss der Mindestlohnkommission 2025). */
export function minimumWageCents(date = todayLocal()): number {
  return date >= '2027-01-01' ? 1460 : date >= '2026-01-01' ? 1390 : 1282;
}

/** Dezimalzahl mit Komma parsen ("7,5" → 7.5); NaN bei ungültig. */
export function parseDecimal(s: string): number {
  const t = s.trim().replace(',', '.');
  return /^\d+(\.\d+)?$/.test(t) ? Number(t) : NaN;
}
